import { useEffect, useRef } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { useStore } from '../store/useStore';

const LABEL: Record<string, string> = {
  updateStage: 'Save stage',
  addStage: 'Add stage',
  deleteStage: 'Delete stage',
  reorderStages: 'Reorder stages',
  updateInvoice: 'Save invoice',
  addInvoice: 'Add invoice',
  deleteInvoice: 'Delete invoice',
  updateProject: 'Save project',
  addProject: 'Create project',
  deleteProject: 'Delete project',
  addClient: 'Add client',
  updateClient: 'Save client',
  deleteClient: 'Delete client',
  addMessage: 'Send message',
  resolveThread: 'Resolve thread',
  deleteDocument: 'Delete document',
  updateTender: 'Salvesta hanke otsus',
  deleteTender: 'Kustuta hange',
  addUser: 'Add user',
  updateUser: 'Save user',
  deleteUser: 'Delete user',
};

const AUTO_DISMISS_MS = 10_000;

export default function ErrorLog() {
  const errors = useStore((s) => s.errors);
  const dismissError = useStore((s) => s.dismissError);
  const clearErrors = useStore((s) => s.clearErrors);

  // Auto-dismiss each error after AUTO_DISMISS_MS
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    for (const err of errors) {
      if (!timersRef.current.has(err.id)) {
        const t = setTimeout(() => {
          dismissError(err.id);
          timersRef.current.delete(err.id);
        }, AUTO_DISMISS_MS);
        timersRef.current.set(err.id, t);
      }
    }
    // Clean up timers for dismissed errors
    for (const [id, t] of timersRef.current.entries()) {
      if (!errors.find((e) => e.id === id)) {
        clearTimeout(t);
        timersRef.current.delete(id);
      }
    }
  }, [errors, dismissError]);

  // Cleanup on unmount
  useEffect(() => () => {
    for (const t of timersRef.current.values()) clearTimeout(t);
  }, []);

  if (errors.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80 max-h-[70vh] overflow-y-auto pointer-events-none">
      {/* Clear all button */}
      {errors.length > 1 && (
        <div className="pointer-events-auto flex justify-end">
          <button
            onClick={clearErrors}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 border border-gray-600 shadow"
          >
            <Trash2 size={11} />
            Clear all ({errors.length})
          </button>
        </div>
      )}

      {errors.map((err) => (
        <div
          key={err.id}
          className="pointer-events-auto flex items-start gap-3 bg-red-950 border border-red-700 text-white rounded-xl shadow-xl px-4 py-3"
        >
          <AlertTriangle size={15} className="text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-red-300 mb-0.5">
              {LABEL[err.operation] ?? err.operation} failed
            </div>
            <div className="text-xs text-red-100 break-words">{err.message}</div>
            <div className="text-[10px] text-red-500 mt-1">
              {new Date(err.timestamp).toLocaleTimeString()}
            </div>
          </div>
          <button
            onClick={() => dismissError(err.id)}
            className="text-red-400 hover:text-red-200 shrink-0 mt-0.5"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
